/* eslint-disable @next/next/no-img-element */

import { CSSProperties, useEffect, useRef, useState } from 'react';
import { Button, Icon } from '@/components/ui';
import useNavigate from '@/hooks/use-navigate';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { cn } from '@/utils';
import { buildGumletUrl, GUMLET_PRESETS } from '@/utils/gumlet';
import { resolveStorefrontImage } from '@/utils/resolve-storefront-image';
import { resolveHeroGradient } from './hero-gradient-presets';
import ImageCarousel, { CarouselHandle } from './image-carousel/image-carousel';

type HeroSectionProps = {
  title: {
    text: string;
    color: string;
    fontSize: string;
    fontWeight: string;
  };
  subtitle: {
    text: string;
    color: string;
    fontSize: string;
    fontWeight: string;
  };
  button: {
    text: string;
    href: string;
    startIcon: string;
    endIcon: string;
  };
  images: string[];
  background: {
    type: 'IMAGE' | 'VIDEO' | 'COLOR';
    value: string;
    imageValue?: string;
    gradient?: string;
  };
  mobileGap?: number;
  contentPaddingStyle?: CSSProperties;
  headingLevel?: 1 | 2;
};

const HeroBannerWithCarouselSection = ({
  title,
  subtitle,
  button,
  images,
  background,
  mobileGap,
  contentPaddingStyle,
  headingLevel = 1,
}: HeroSectionProps) => {
  const resolveThemeColor = useResolveThemeColor();
  const navigate = useNavigate();
  const carouselRef = useRef<CarouselHandle | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [thumbStart, setThumbStart] = useState(0);
  const VISIBLE_THUMBS = 5;
  const MAX_IMAGES = 5;

  const visibleImages = images.slice(0, MAX_IMAGES);

  useEffect(() => {
    navigate.prefetch(button.href || '/');
  }, [navigate, button.href]);

  useEffect(() => {
    if (selectedIndex < thumbStart) {
      setThumbStart(selectedIndex);
    } else if (selectedIndex >= thumbStart + VISIBLE_THUMBS) {
      setThumbStart(selectedIndex - (VISIBLE_THUMBS - 1));
    }
    setThumbStart(prev =>
      Math.max(0, Math.min(prev, Math.max(0, visibleImages.length - VISIBLE_THUMBS))),
    );
  }, [selectedIndex, thumbStart, visibleImages.length]);

  const TitleTag = headingLevel === 1 ? 'h1' : 'h2';

  const bgImageUrl =
    background.type === 'IMAGE'
      ? resolveStorefrontImage(
          background.imageValue || background.value,
          undefined,
          GUMLET_PRESETS.heroBackground,
        )
      : undefined;
  const resolvedGradient =
    background.type === 'IMAGE' ? resolveHeroGradient(background.gradient) : undefined;

  return (
    <div
      className={cn(
        'relative flex min-h-[80dvh] w-full items-center',
        'bg-cover bg-center bg-no-repeat',
      )}
      style={{
        backgroundImage: bgImageUrl ? `url(${bgImageUrl})` : undefined,
        backgroundColor:
          background.type === 'COLOR' ? resolveThemeColor(background.value) : undefined,
      }}
    >
      {background.type === 'IMAGE' && resolvedGradient && (
        <div
          className="pointer-events-none absolute inset-0"
          style={{ backgroundImage: resolvedGradient }}
        />
      )}
      <div
        className="relative z-10 container mx-auto flex flex-col items-center justify-between gap-[var(--hero-mobile-gap)] @lg:flex-row @lg:gap-16"
        style={
          {
            '--hero-mobile-gap': mobileGap !== undefined ? `${mobileGap}px` : '4rem',
            ...contentPaddingStyle,
          } as CSSProperties
        }
      >
        <div className="flex max-w-lg flex-col justify-center gap-4 @lg:w-1/2">
          <TitleTag
            className={cn('text-center text-3xl font-medium @md:text-4xl @lg:text-start', {
              'font-light': title.fontWeight === 'light',
              'font-normal': title.fontWeight === 'normal',
              'font-semibold': title.fontWeight === 'semi-bold',
              'font-bold': title.fontWeight === 'bold',
            })}
            style={{ color: resolveThemeColor(title.color), fontSize: title.fontSize }}
          >
            {title.text}
          </TitleTag>
          <p
            className={cn('text-center text-lg text-gray-900 @lg:text-start', {
              'font-light': subtitle.fontWeight === 'light',
              'font-normal': subtitle.fontWeight === 'normal',
              'font-semibold': subtitle.fontWeight === 'semi-bold',
              'font-bold': subtitle.fontWeight === 'bold',
            })}
            style={{ color: resolveThemeColor(subtitle.color), fontSize: subtitle.fontSize }}
          >
            {subtitle.text}
          </p>
          <div className="mb-6 flex justify-center @lg:mb-0 @lg:justify-start">
            <Button
              color="primary"
              onPress={() => navigate(button.href || '/')}
              onMouseEnter={() => navigate.prefetch(button.href || '/')}
              onFocus={() => navigate.prefetch(button.href || '/')}
              className="font-medium text-white @lg:w-fit"
              startContent={button.startIcon ? <Icon icon={button.startIcon} /> : undefined}
              endContent={button.endIcon ? <Icon icon={button.endIcon} /> : undefined}
            >
              {button.text}
            </Button>
          </div>
          <div className="mt-4 hidden items-center gap-3 @lg:flex">
            <div className="flex gap-3 overflow-hidden p-2">
              {visibleImages.slice(thumbStart, thumbStart + VISIBLE_THUMBS).map((src, idx) => {
                const realIdx = thumbStart + idx;
                return (
                  <button
                    key={realIdx}
                    type="button"
                    aria-label={`Show hero image ${realIdx + 1}`}
                    aria-current={realIdx === selectedIndex ? 'true' : undefined}
                    onClick={() => {
                      carouselRef.current?.scrollTo(realIdx);
                      setSelectedIndex(realIdx);
                    }}
                    className={cn(
                      'relative rounded-md bg-white transition-transform',
                      realIdx === selectedIndex ? 'ring-primary scale-105 ring-2' : 'ring-0',
                    )}
                  >
                    <div className="overflow-hidden rounded-md">
                      <img
                        alt={`thumb-${realIdx}`}
                        src={buildGumletUrl(src, { width: 192, quality: 70 })}
                        width={96}
                        height={96}
                        loading="lazy"
                        decoding="async"
                        className="h-24 w-24 rounded-md object-cover"
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="@lg:w-1/2">
          <ImageCarousel
            ref={carouselRef}
            images={visibleImages}
            onSelect={index => setSelectedIndex(index)}
          />
        </div>
      </div>
    </div>
  );
};
export default HeroBannerWithCarouselSection;
